import isEmpty from 'lodash/isEmpty';
import {
  getFinalWirSections,
  getInterventionCloseOutSections,
  getIsSuperUser,
  getKomTechnicalWirSections,
  getPhases,
} from 'pages/WellGuardian/WIR/utils';
import { Phase, Section, WIRCreateJourneyStepsKeys } from 'pages/WellGuardian/WIR/wir.constants';
import { useMemo } from 'react';
import { CountryData, WirPauseStates } from 'types/wellGuardian';
import { useSelectUser } from '../../../../redux/User/selectors';
import {
  useActiveWIRStep,
  useCountryState,
  useSelectedSection,
  useSelectedWIR,
  useWIRUsers,
} from '../../../../redux/WellGuardian/selectors';

import { getIsOnlyReadWIRUser } from '../utils/getIsOnlyReadWIRUser';
import { getNonReactiveSections, getReactiveSections } from '../utils/getSectionByCountry';
import {
  getEngineeringWorkSectionKey,
  getFinalWirSectionKey,
  getInterventionCloseOutSectionKey,
  getKickOffSectionKey,
} from '../utils/getSectionData';
import { getWirStageGate2Sections } from '../utils/getWirStageGate2Sections';
import { getWirStageGate3Sections } from '../utils/getWirStageGate3Sections';
import { getWirStageGate5Sections } from '../utils/getWirStageGate5Sections';

export const useEditableData = (phase: Phase) => {
  const activeStep = useActiveWIRStep();
  const user = useSelectUser();
  const users = useWIRUsers();
  const wir = useSelectedWIR();
  const countryState = useCountryState();
  const bothSections = useSelectedSection();
  const kickOfMeetPhase: Phase = getKickOffSectionKey(countryState);
  const finalWirPhase = getFinalWirSectionKey(countryState);
  const interventionCloseOutPhase = getInterventionCloseOutSectionKey(countryState);
  const engineeringWorkPhase = getEngineeringWorkSectionKey(countryState);
  const wirPhases = getPhases(wir, countryState);

  const sectionKey = useMemo(
    () =>
      wir?.isUnplannedReactiveScope
        ? getReactiveSections(wir, countryState).find(item => item.label === bothSections)?.value
        : getNonReactiveSections(wir, countryState).find(item => item.label === bothSections)
            ?.value,
    [wir, countryState, bothSections],
  );

  const assignedToUserId = useMemo(
    () =>
      users?.find(
        item =>
          item.userId ===
          wir?.phases?.[phase as Phase]?.sections?.[sectionKey as Section]?.assignedTo,
      )?.userId,
    [wir, users, phase, sectionKey],
  );

  const checkSignOff = useMemo(() => {
    // SignOff
    if (countryState !== CountryData.denmark) {
      return !getKomTechnicalWirSections(wir, countryState)
        .filter(item => item.value !== 'signOffTechnicalWIR')
        .some(item => !wir?.phases?.[kickOfMeetPhase]?.sections?.[item.value as Section]?.isTicked);
    } else {
      return !getWirStageGate2Sections(wir)
        .filter(item => item.value !== 'signOffTechnicalWIR')
        .some(item => !wir?.phases?.stageGate2?.sections?.[item.value as Section]?.isTicked);
    }
  }, [countryState, kickOfMeetPhase, wir]);

  const checkFinalWirSignOff = useMemo(() => {
    // FinalWirSignOff
    if (
      phase !== WIRCreateJourneyStepsKeys.REACTIVE_SCOPE &&
      countryState !== CountryData.denmark
    ) {
      return !getFinalWirSections(wir, countryState)
        .filter(item => item.value !== 'finalWirSignOff')
        .some(item => !wir?.phases?.[finalWirPhase]?.sections?.[item.value as Section]?.isTicked);
    /* istanbul ignore next */
    } else if (
      phase === WIRCreateJourneyStepsKeys.REACTIVE_SCOPE &&
      /* istanbul ignore next */
      countryState === CountryData.argentina
    ) {
      return !getFinalWirSections(wir, countryState)
        .filter(item => item.value !== 'finalWirSignOff')
        .some(item => !wir?.phases?.['reactiveScope']?.sections?.[item.value as Section]?.isTicked);
    } else if (
      phase !== WIRCreateJourneyStepsKeys.REACTIVE_SCOPE &&
      countryState === CountryData.denmark
    ) {
      return !getWirStageGate3Sections(wir)
        .filter(item => item.value !== 'finalWirSignOff')
        .some(item => !wir?.phases?.stageGate3?.sections?.[item.value as Section]?.isTicked);
    /* istanbul ignore next */
    } else {
      return true;
    }
  }, [countryState, finalWirPhase, phase, wir]);

  const checkTechFinalSignOff = useMemo(() => {
    // InterventionCloseOut
    if (countryState !== CountryData.denmark) {
      return !getInterventionCloseOutSections(wir, countryState)
        .filter(item => item.value !== 'hqTechnicalfinalInterventionSignOff')
        .some(
          item =>
            !wir?.phases?.[interventionCloseOutPhase]?.sections?.[item.value as Section]?.isTicked,
        );
    } else if (countryState === CountryData.denmark) {
      return !getWirStageGate5Sections(wir)
        .filter(item => item.value !== 'hqTechnicalfinalInterventionSignOff')
        .some(item => !wir?.phases?.stageGate5?.sections?.[item.value as Section]?.isTicked);
    }
  }, [countryState, interventionCloseOutPhase, wir]);

  const allNonApprovalSectionsInPhaseCompleted = useMemo(() => {
    // Allow approval only if all other sections are ticked
    /* istanbul ignore next */
    if (sectionKey === 'signOffTechnicalWIR') {
      return checkSignOff;
    } else if (sectionKey === 'finalWirSignOff') {
      return checkFinalWirSignOff;
    /* istanbul ignore next */
    } else if (sectionKey === 'hqTechnicalfinalInterventionSignOff') {
      return checkTechFinalSignOff;
    }
  }, [sectionKey, checkSignOff, checkFinalWirSignOff, checkTechFinalSignOff]);

  const isSectionKeySignOff =
    sectionKey === 'signOffTechnicalWIR' ||
    sectionKey === 'finalWirSignOff' ||
    sectionKey === 'hqTechnicalfinalInterventionSignOff';

  const isNotPaused = wir?.pausedState !== WirPauseStates.PAUSED;

  const sectionKeySignOffReturn = useMemo(() => {
    return (
      ((activeStep === wirPhases.findIndex(item => item === phase) &&
        !wir?.phases?.[phase]?.sections?.[sectionKey as Section]?.isTicked) ||
        getIsSuperUser(users, user, countryState)) &&
      Boolean(allNonApprovalSectionsInPhaseCompleted) &&
      /* istanbul ignore next */
      isNotPaused
    );
  }, [
    activeStep,
    allNonApprovalSectionsInPhaseCompleted,
    countryState,
    isNotPaused,
    phase,
    sectionKey,
    user,
    users,
    wir?.phases,
    wirPhases,
  ]);

  const isEngineeringWorkPhaseReturn = useMemo(() => {
    return (
      ((!isEmpty(user?.igg) &&
        assignedToUserId === user?.igg &&
        /* istanbul ignore next */
        activeStep === getPhases(wir, countryState).findIndex(item => item === phase) &&
        /* istanbul ignore next */
        !wir?.phases?.[phase]?.sections?.[sectionKey as Section]?.isTicked) ||
        wir?.actions?.some(
          action =>
            action.actioneeName === users?.find(item => item.userId === user?.igg)?.userName,
        ) ||
        getIsSuperUser(users, user, countryState)) &&
      /* istanbul ignore next */
      isNotPaused
    );
  }, [
    activeStep,
    assignedToUserId,
    countryState,
    isNotPaused,
    phase,
    sectionKey,
    user,
    users,
    wir,
  ]);

  const isEditable = useMemo(() => {
    const isNotPaused = wir?.pausedState !== WirPauseStates.PAUSED;
    const isEngineeringWorkPhase =
      wirPhases[activeStep] === engineeringWorkPhase &&
      phase === engineeringWorkPhase &&
      sectionKey === 'reviewChecklistActions';

    if (isSectionKeySignOff) {
      return sectionKeySignOffReturn;
    }

    if (
      (isEngineeringWorkPhase && sectionKey === 'reviewChecklistActions') ||
      (countryState === CountryData.denmark &&
        sectionKey === 'reviewChecklistActions' &&
        /* istanbul ignore next */
        getPhases(wir, countryState)[activeStep] === phase)
    ) {
      return isEngineeringWorkPhaseReturn;
    }

    /* istanbul ignore next */
    if (getIsOnlyReadWIRUser(users, user)) {
      return false;
    }

    return (
      ((!isEmpty(user?.igg) &&
        assignedToUserId === user?.igg &&
        /* istanbul ignore next */
        activeStep === wirPhases.findIndex(item => item === phase) &&
        /* istanbul ignore next */
        !wir?.phases?.[phase]?.sections?.[sectionKey as Section]?.isTicked) ||
        getIsSuperUser(users, user, countryState)) &&
      /* istanbul ignore next */
      isNotPaused
    );
  }, [
    wir,
    wirPhases,
    activeStep,
    engineeringWorkPhase,
    phase,
    sectionKey,
    isSectionKeySignOff,
    countryState,
    users,
    user,
    assignedToUserId,
    sectionKeySignOffReturn,
    isEngineeringWorkPhaseReturn,
  ]);

  return {
    isEditable,
  };
};
